import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'

export default function Setup() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [step, setStep] = useState(1)

  // Organization info
  const [orgName, setOrgName] = useState('')
  const [orgSlug, setOrgSlug] = useState('')

  // Faculties
  const [faculties, setFaculties] = useState([''])

  // Departments (per faculty)
  const [departments, setDepartments] = useState({})

  // Levels (per department)
  const [levels, setLevels] = useState({})

  const addFaculty = () => {
    setFaculties([...faculties, ''])
  }

  const removeFaculty = (index) => {
    const newFaculties = faculties.filter((_, i) => i !== index)
    setFaculties(newFaculties)
  }

  const updateFaculty = (index, value) => {
    const newFaculties = [...faculties]
    newFaculties[index] = value
    setFaculties(newFaculties)
  }

  const addDepartment = (facultyIndex) => {
    setDepartments({
      ...departments,
      [facultyIndex]: [...(departments[facultyIndex] || []), '']
    })
  }

  const updateDepartment = (facultyIndex, deptIndex, value) => {
    const newDepts = [...departments[facultyIndex]]
    newDepts[deptIndex] = value
    setDepartments({
      ...departments,
      [facultyIndex]: newDepts
    })
  }

  const addLevel = (facultyIndex, deptIndex) => {
    const key = `${facultyIndex}-${deptIndex}`
    setLevels({
      ...levels,
      [key]: [...(levels[key] || []), '']
    })
  }

  const updateLevel = (facultyIndex, deptIndex, levelIndex, value) => {
    const key = `${facultyIndex}-${deptIndex}`
    const newLevels = [...levels[key]]
    newLevels[levelIndex] = value
    setLevels({
      ...levels,
      [key]: newLevels
    })
  }

  const handleSubmit = async () => {
    setError('')
    setLoading(true)

    try {
      const { data: { user } } = await supabase.auth.getUser()

      // Create organization
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .insert({ name: orgName, slug: orgSlug })
        .select()
        .single()

      if (orgError) throw orgError

      // Update user's org_id
      await supabase
        .from('users')
        .update({ org_id: org.id })
        .eq('id', user.id)

      // Create hierarchy
      for (let i = 0; i < faculties.length; i++) {
        const facultyName = faculties[i].trim()
        if (!facultyName) continue

        // Create Faculty (depth 1)
        const { data: faculty, error: facultyError } = await supabase
          .from('groups')
          .insert({
            org_id: org.id,
            name: facultyName,
            depth: 1,
            parent_id: null
          })
          .select()
          .single()

        if (facultyError) throw facultyError

        // Create Departments for this Faculty
        const depts = departments[i] || []
        for (let j = 0; j < depts.length; j++) {
          const deptName = depts[j].trim()
          if (!deptName) continue

          const { data: department, error: deptError } = await supabase
            .from('groups')
            .insert({
              org_id: org.id,
              name: deptName,
              depth: 2,
              parent_id: faculty.id
            })
            .select()
            .single()

          if (deptError) throw deptError

          // Create Levels for this Department
          const levelsKey = `${i}-${j}`
          const levelsList = levels[levelsKey] || []
          for (const levelName of levelsList) {
            if (!levelName.trim()) continue

            const { error: levelError } = await supabase
              .from('groups')
              .insert({
                org_id: org.id,
                name: levelName.trim(),
                depth: 3,
                parent_id: department.id
              })

            if (levelError) throw levelError
          }
        }
      }

      // Create default affirmations
      const defaultAffirmations = [
        'Great to see you! Keep up the excellent attendance!',
        'You\'re doing amazing! Thank you for being here!',
        'Your presence matters! Well done!',
        'Consistency is key! You\'re nailing it!',
        'Thank you for showing up! You\'re awesome!'
      ]

      await supabase
        .from('affirmations')
        .insert(
          defaultAffirmations.map(text => ({
            org_id: org.id,
            text,
            created_by: user.id
          }))
        )

      navigate('/dashboard')
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Setup Your Organization</h1>
          <p className="text-gray-600 mt-2">Create your organizational hierarchy</p>
        </div>

        <div className="bg-white rounded-lg shadow p-8">
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded mb-6 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Organization */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900">Organization Details</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization Name *
                </label>
                <input
                  type="text"
                  required
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Covenant University"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Slug *
                </label>
                <input
                  type="text"
                  required
                  value={orgSlug}
                  onChange={(e) => setOrgSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., covenant-university"
                />
                <p className="text-sm text-gray-500 mt-1">
                  This will be used in invite links
                </p>
              </div>

              <button
                onClick={() => setStep(2)}
                disabled={!orgName || !orgSlug}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                Next: Add Faculties
              </button>
            </div>
          )}

          {/* Step 2: Faculties */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900">Add Faculties</h2>

              {faculties.map((faculty, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={faculty}
                    onChange={(e) => updateFaculty(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`Faculty ${index + 1}`}
                  />
                  {faculties.length > 1 && (
                    <button
                      onClick={() => removeFaculty(index)}
                      className="px-4 py-2 text-red-600 border border-red-600 rounded-md hover:bg-red-50"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}

              <button
                onClick={addFaculty}
                className="w-full border-2 border-dashed border-gray-300 py-2 px-4 rounded-md hover:border-blue-500 hover:text-blue-500 text-gray-600"
              >
                + Add Faculty
              </button>

              <div className="flex gap-4">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 border border-gray-300 py-2 px-4 rounded-md hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!faculties.some(f => f.trim())}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Next: Add Departments
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Departments */}
          {step === 3 && (
            <div className="space-y-8">
              <h2 className="text-xl font-bold text-gray-900">Add Departments</h2>

              {faculties.map((faculty, fIndex) => {
                if (!faculty.trim()) return null
                
                return (
                  <div key={fIndex} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-4">{faculty}</h3>
                    
                    {(departments[fIndex] || []).map((dept, dIndex) => (
                      <div key={dIndex} className="mb-2">
                        <input
                          type="text"
                          value={dept}
                          onChange={(e) => updateDepartment(fIndex, dIndex, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={`Department ${dIndex + 1}`}
                        />
                      </div>
                    ))}

                    <button
                      onClick={() => addDepartment(fIndex)}
                      className="w-full border-2 border-dashed border-gray-300 py-2 px-4 rounded-md hover:border-blue-500 hover:text-blue-500 text-gray-600 text-sm mt-2"
                    >
                      + Add Department to {faculty}
                    </button>
                  </div>
                )
              })}

              <div className="flex gap-4">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 border border-gray-300 py-2 px-4 rounded-md hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep(4)}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
                >
                  Next: Add Levels
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Levels */}
          {step === 4 && (
            <div className="space-y-8">
              <h2 className="text-xl font-bold text-gray-900">Add Levels</h2>

              {faculties.map((faculty, fIndex) => {
                if (!faculty.trim()) return null
                const depts = departments[fIndex] || []
                
                return depts.map((dept, dIndex) => {
                  if (!dept.trim()) return null
                  const levelsKey = `${fIndex}-${dIndex}`
                  const levelsList = levels[levelsKey] || []

                  return (
                    <div key={levelsKey} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">{dept}</h3>
                      <p className="text-sm text-gray-600 mb-4">in {faculty}</p>
                      
                      {levelsList.map((level, lIndex) => (
                        <div key={lIndex} className="mb-2">
                          <input
                            type="text"
                            value={level}
                            onChange={(e) => updateLevel(fIndex, dIndex, lIndex, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder={`Level ${lIndex + 1} (e.g., Year 1, 100 Level)`}
                          />
                        </div>
                      ))}

                      <button
                        onClick={() => addLevel(fIndex, dIndex)}
                        className="w-full border-2 border-dashed border-gray-300 py-2 px-4 rounded-md hover:border-blue-500 hover:text-blue-500 text-gray-600 text-sm mt-2"
                      >
                        + Add Level to {dept}
                      </button>
                    </div>
                  )
                })
              })}

              <div className="flex gap-4">
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 border border-gray-300 py-2 px-4 rounded-md hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50 font-medium"
                >
                  {loading ? 'Creating...' : 'Complete Setup'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}